import { FormEvent, useEffect, useState } from "react";
import { useAuthors, useCreateAuthor, useUpdateAuthor, useDeleteAuthor } from "@/hooks/useAuthors";
import { Author } from "@/types/authors";
import { PageHeader, Card, Button } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import ImageUploader from "@/components/ImageUploader";

interface AuthorFormState {
  displayName: string;
  title: string;
  avatarUrl: string;
}

const emptyForm: AuthorFormState = {
  displayName: "",
  title: "",
  avatarUrl: "",
};

const Authors = () => {
  const { data: authors, isLoading } = useAuthors();
  const createMutation = useCreateAuthor();
  const updateMutation = useUpdateAuthor();
  const deleteMutation = useDeleteAuthor();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<Author | null>(null);
  const [form, setForm] = useState<AuthorFormState>(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (editingAuthor) {
      setForm({
        displayName: editingAuthor.displayName,
        title: editingAuthor.title || "",
        avatarUrl: editingAuthor.avatarUrl || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [editingAuthor]);

  const openCreateModal = () => {
    setEditingAuthor(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (author: Author) => {
    setEditingAuthor(author);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingAuthor(null);
      setForm(emptyForm);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.displayName.trim()) return;

    const payload = {
      displayName: form.displayName.trim(),
      title: form.title.trim() || null,
      avatarUrl: form.avatarUrl.trim() || null,
    };

    try {
      if (editingAuthor) {
        await updateMutation.mutateAsync({ authorId: editingAuthor.authorId, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      closeModal();
    } catch (error) {
      console.error("Failed to save author:", error);
    }
  };

  const handleDelete = async (authorId: string) => {
    try {
      await deleteMutation.mutateAsync(authorId);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error("Failed to delete author:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="page">
        <PageHeader
          label="Content"
          title="Authors"
          description="Manage authors for news posts and help articles."
        />
        <Card>
          <p className="meta" style={{ textAlign: "center", padding: "32px 0" }}>Loading...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        label="Content"
        title="Authors"
        description="Manage authors for news posts and help articles."
        actions={
          <Button onClick={openCreateModal}>
            <Icon name="plus" />
            Add Author
          </Button>
        }
      />

      {authors && authors.length > 0 ? (
        <div className="grid g4">
          {authors.map((author) => (
            <div
              key={author.authorId}
              className="acard"
              style={deleteConfirmId === author.authorId ? { borderColor: "var(--err-bd)" } : undefined}
            >
              <div className="top">
                {author.avatarUrl ? (
                  <img
                    src={author.avatarUrl}
                    alt={author.displayName}
                    className="av"
                    style={{ objectFit: "cover" }}
                  />
                ) : (
                  <span className="av g">{author.displayName.charAt(0).toUpperCase()}</span>
                )}
                <span className="tx">
                  <b>{author.displayName}</b>
                  {author.title && <span>{author.title}</span>}
                </span>
              </div>
              <div className="bot">
                {deleteConfirmId === author.authorId ? (
                  <>
                    <span
                      style={{
                        fontSize: "11.5px",
                        color: "var(--err)",
                        fontWeight: 620,
                        alignSelf: "center",
                        flex: 1,
                      }}
                    >
                      Delete this author?
                    </span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(author.authorId)}
                      loading={deleteMutation.isPending}
                    >
                      Confirm
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirmId(null)}
                      disabled={deleteMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" size="sm" onClick={() => openEditModal(author)}>
                      <Icon name="edit" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="dan-gho"
                      onClick={() => setDeleteConfirmId(author.authorId)}
                    >
                      <Icon name="trash" />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <div className="empty">
            <div className="empty-i">
              <Icon name="users" />
            </div>
            <b>No authors yet</b>
            <p>Add authors to attribute news posts and help articles.</p>
            <Button onClick={openCreateModal}>Create your first author</Button>
          </div>
        </Card>
      )}

      {/* Author Modal */}
      {isModalOpen && (
        <div
          className="ov open"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="mod">
            <div className="mod-h">
              <div className="tx">
                <h3>{editingAuthor ? "Edit Author" : "Create Author"}</h3>
                <p>{editingAuthor ? "Update author details." : "Add a new author to your team."}</p>
              </div>
              <button className="icon-btn" onClick={closeModal} disabled={isSaving}>
                <Icon name="x" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mod-b">
                <div className="fg">
                  <label className="fl">
                    Display Name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.displayName}
                    onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
                    className="inp"
                    placeholder="John Doe"
                    required
                    disabled={isSaving}
                  />
                </div>

                <div className="fg">
                  <label className="fl">Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    className="inp"
                    placeholder="Health & Wellness Expert"
                    disabled={isSaving}
                  />
                </div>

                <div className="fg">
                  <label className="fl">Avatar</label>
                  <ImageUploader
                    value={form.avatarUrl || null}
                    onChange={(url) => setForm((prev) => ({ ...prev, avatarUrl: url || "" }))}
                    placeholder="Upload avatar image"
                  />
                </div>
              </div>

              <div className="mod-f">
                <Button type="button" variant="ghost" onClick={closeModal} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="submit" loading={isSaving}>
                  {editingAuthor ? "Save Changes" : "Create Author"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Authors;
